import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2, AlertCircle, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useUTM } from "../../hooks/useUTM";
import Reveal from "../Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_KEYS = [
  { key: "services_options.meta_ads", slug: "meta-ads" },
  { key: "services_options.google_ads", slug: "google-ads" },
  { key: "services_options.seo", slug: "seo" },
  { key: "services_options.creative", slug: "creative" },
  { key: "services_options.social", slug: "social" },
  { key: "services_options.web", slug: "web" },
];

function Field({ id, label, type = "text", placeholder, value, onChange, error, testId, required }) {
  return (
    <div>
      <label htmlFor={id} className="label-base">
        {label}
        {required ? " *" : ""}
      </label>
      <input
        id={id} type={type} data-testid={testId}
        value={value} onChange={onChange} placeholder={placeholder}
        className="input-base" required={required}
      />
      {error ? (
        <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />{error}
        </p>
      ) : null}
    </div>
  );
}

export default function CTASection({
  eyebrow, title, titleAccent, subtitle,
  defaultServiceSlug = "", defaultServiceLabel = "",
  hideHeader = false,
  layout = "split",  // 'split' or 'stacked'
}) {
  const { t, lang } = useLanguage();
  const { utm, referrer } = useUTM();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service: defaultServiceLabel || "",
    serviceSlug: defaultServiceSlug || "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  // Sync defaultServiceLabel/Slug if they change (when navigating between landings)
  useEffect(() => {
    setForm((p) => ({
      ...p,
      service: defaultServiceLabel || p.service,
      serviceSlug: defaultServiceSlug || p.serviceSlug,
    }));
  }, [defaultServiceLabel, defaultServiceSlug]);

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleServiceChange = (e) => {
    const label = e.target.value;
    const found = SERVICE_KEYS.find((s) => t(s.key) === label);
    setForm((p) => ({ ...p, service: label, serviceSlug: found?.slug || "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t("form.required");
    if (!form.email.trim()) next.email = t("form.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = t("form.invalid_email");
    if (!form.phone.trim()) next.phone = t("form.required");
    if (!form.service.trim()) next.service = t("form.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      const landingPage = typeof window !== "undefined" ? window.location.pathname : "";
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        service_slug: form.serviceSlug,
        message: form.message,
        lang,
        utm,
        referrer,
        landing_page: landingPage,
      });
      if (res.data?.success) {
        setStatus("success");
        // Redirect to thank you page (GTM conversion tracking)
        navigate(`/${lang}/tesekkurler`);
      } else {
        throw new Error("Bad response");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Lead submit failed", err);
      setStatus("error");
      toast.error(t("form.error"));
    }
  };

  if (hideHeader) {
    // Embedded mode (e.g. ContactPage) — assume parent provides the section/container.
    return (
      <Reveal as="form" delay={0.08} onSubmit={handleSubmit} noValidate
        id="contact"
        className="rounded-2xl bg-white p-6 sm:p-8 lg:p-10 border border-mist shadow-[0_24px_60px_-30px_rgba(10,10,10,0.18)]"
        data-testid="lead-form"
      >
        <FormBody
          form={form} errors={errors} status={status}
          update={update} handleServiceChange={handleServiceChange} t={t}
        />
      </Reveal>
    );
  }

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative bg-cream py-section sm:py-section-lg border-t border-mist overflow-hidden"
    >
      <div className="pointer-events-none absolute -bottom-32 -end-24 h-[460px] w-[460px] rounded-full bg-gold/15 blur-3xl" />
      <div className="container-page relative">
        <div className={`grid gap-12 ${layout === "stacked" ? "" : "lg:grid-cols-12 lg:gap-16"} items-start`}>
          <Reveal as="div" className="lg:col-span-5">
            <span className="eyebrow">
              <Sparkles className="h-3 w-3 text-gold" />
              {eyebrow || t("cta.eyebrow")}
            </span>
            <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
              {title || t("cta.title_pre")}{" "}
              <span className="gold-foil-text">{titleAccent || t("cta.title_accent")}</span>
            </h2>
            <p className="mt-6 text-body-lg text-ink/65 max-w-md leading-relaxed">
              {subtitle || t("cta.subtitle")}
            </p>
            <div className="hairline-gold mt-10" />

            <a
              href="https://wa.me/908508884865"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-sm text-ink/80 hover:text-ink transition-colors group"
              data-testid="cta-whatsapp-direct"
            >
              <MessageCircle className="h-4 w-4 text-gold" />
              <span className="border-b border-ink/20 group-hover:border-ink pb-0.5">
                {t("cta.whatsapp_direct")}
              </span>
            </a>
          </Reveal>

          <Reveal as="form" delay={0.08} onSubmit={handleSubmit} noValidate
            className="lg:col-span-7 rounded-2xl bg-white p-6 sm:p-8 lg:p-10 border border-mist shadow-[0_24px_60px_-30px_rgba(10,10,10,0.18)]"
            data-testid="lead-form"
          >
            <FormBody
              form={form} errors={errors} status={status}
              update={update} handleServiceChange={handleServiceChange} t={t}
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/** Internal — extracted so we can reuse the form body in both standalone and
 *  embedded (Contact page) layouts without duplicating fields. */
function FormBody({ form, errors, status, update, handleServiceChange, t }) {
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t("form.name")} placeholder={t("form.name_ph")}
          value={form.name} onChange={update("name")} error={errors.name}
          testId="lead-input-name" required />
        <Field id="email" type="email" label={t("form.email")} placeholder={t("form.email_ph")}
                value={form.email} onChange={update("email")} error={errors.email}
                testId="lead-input-email" required />
              <Field id="phone" label={t("form.phone")} placeholder={t("form.phone_ph")}
                value={form.phone} onChange={update("phone")} error={errors.phone}
                testId="lead-input-phone" required />
              <div>
                <label htmlFor="service" className="label-base">{t("form.service")} *</label>
                <select
                  id="service"
                  data-testid="lead-input-service"
                  value={form.service}
                  onChange={handleServiceChange}
                  className="input-base appearance-none pr-10"
                  required
                >
                  <option value="">{t("form.service_ph")}</option>
                  {SERVICE_KEYS.map((s) => (
                    <option key={s.slug} value={t(s.key)}>{t(s.key)}</option>
                  ))}
                </select>
                {errors.service ? (
                  <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{errors.service}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="label-base">{t("form.message")}</label>
              <textarea
                id="message"
                data-testid="lead-input-message"
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder={t("form.message_ph")}
                className="input-base resize-none"
              />
            </div>

            <motion.button
              type="submit"
              data-testid="lead-submit-button"
              disabled={status === "submitting"}
              whileHover={{ scale: status === "submitting" ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="btn-gold mt-8 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? (
                <><Loader2 className="h-4 w-4 animate-spin" />{t("form.submitting")}</>
              ) : status === "success" ? (
                <><CheckCircle2 className="h-4 w-4" />{t("form.success")}</>
              ) : (
                <><Send className="h-4 w-4" />{t("form.submit")}</>
              )}
            </motion.button>

            <p className="mt-4 text-xs text-ink/45">{t("form.fine_print")}</p>
    </>
  );
}
